import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, Award, Users, Phone, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data';
import useMeta from '../hooks/useMeta';

export default function About() {
  useMeta(
    'Om LJR Teknik ApS | Din Pålidelige Freelance Partner',
    'Læs historien bag LJR Teknik ApS. Med over 15 års erfaring leverer vi kompromisløs kvalitet og personlig service til dine gaffeltrucks og maskiner.'
  );

  const stats = [
    { label: "Års Erfaring", value: "15+" },
    { label: "Servicerede Maskiner", value: "500+" },
    { label: "Tilfredse Kunder", value: "100%" },
    { label: "Hurtig Udrykning", value: "Ja" }
  ];

  const values = [
    {
      title: "Høj Faglig Kvalitet",
      description: "Jeg går aldrig på kompromis med sikkerheden. Mit arbejde udføres altid efter gældende regler og standarder, og jeg bruger udelukkende godkendte reservedele af højeste kvalitet.",
      icon: ShieldCheck
    },
    {
      title: "Hurtig & Fleksibel",
      description: "Når en maskine bryder sammen, tæller hvert minut. Jeg rykker hurtigt ud med min fuldt udstyrede servicevogn og løser de fleste problemer direkte på din adresse.",
      icon: Clock
    },
    {
      title: "Gennemskuelighed",
      description: "Jeg tror på ærligt samarbejde. Du får altid et klart overblik over omfanget af reparationen og et uforpligtende prisoverslag, så du undgår ubehagelige overraskelser.",
      icon: Award
    }
  ];

  return (
    <div className="bg-zinc-50 min-h-screen">
      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column: Image */}
            <div className="relative h-[350px] md:h-[480px] rounded-2xl overflow-hidden bg-zinc-100 shadow-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/ac914a03-55b8-406d-88cf-a09072b72053/image00010_3.jpeg"
                alt="LJR Teknik ApS Værksted og Service"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Right Column: Text */}
            <div className="space-y-6">
              <h2 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl uppercase">
                Din garanti for professionel maskinservice
              </h2>
              <div className="space-y-4 text-zinc-600 leading-relaxed">
                <p>
                  LJR Teknik ApS blev grundlagt med en klar vision: at levere pålidelig, hurtig og professionel service til virksomheder, der er afhængige af deres gaffeltrucks og maskiner i den daglige drift.
                </p>
                <p>
                  Jeg forstår, at maskinnedbrud koster dyrt i tabt produktivitet og forsinkelser. Derfor har jeg specialiseret mig i hurtig udrykning og forebyggende vedligeholdelse. Jeg servicerer alle mærker af gaffeltrucks (el, gas, diesel) samt mindre maskiner som stablere, pallevogne, minigravere og fejemaskiner.
                </p>
                <p>
                  Jeg er fuldt certificeret og har mange års praktisk erfaring. Med min fuldt udstyrede servicevogn klarer jeg de fleste reparationer direkte hos kunden, hvilket sparer dig for tid og transportomkostninger.
                </p>
              </div>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3 text-sm font-semibold text-white hover:bg-red-700 shadow-md shadow-red-600/10 transition-all"
                >
                  <span>Kontakt mig for service</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-zinc-950 text-white border-y border-zinc-800">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="space-y-2">
                <div className="text-4xl md:text-5xl font-semibold text-white">{stat.value}</div>
                <div className="text-xs md:text-sm font-medium text-zinc-400 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl uppercase">
              Det fundament jeg bygger mit arbejde på
            </p>
            <p className="text-zinc-600">
              Jeg ønsker at være din foretrukne servicepartner på lang sigt. Derfor arbejder jeg altid ud fra tre simple principper.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 md:gap-y-0 md:divide-x divide-zinc-200">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="space-y-4 md:px-8 first:pl-0 last:pr-0">
                  <div className="text-black">
                    <IconComponent className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-zinc-900 uppercase">{value.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-20 bg-zinc-950 text-white border-t border-zinc-800">
        <div className="mx-auto max-w-5xl px-4 text-center space-y-6">
          <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl uppercase">
            Lad mig holde din maskinpark kørende
          </h2>
          <p className="text-zinc-300 max-w-2xl mx-auto text-base">
            Kontakt mig i dag for en uforpligtende snak om dine behov, eller få et skræddersyet tilbud på service eller det lovpligtige sikkerhedseftersyn.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-8 py-4 text-base font-bold text-white hover:bg-red-700 shadow-lg shadow-red-600/20 transition-all"
            >
              <span>Kontakt mig nu</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg border border-white bg-zinc-900 px-8 py-4 text-base font-semibold text-white hover:bg-white hover:text-black transition-all group"
            >
              <Phone className="h-5 w-5 text-white group-hover:text-black transition-colors" />
              <span>Ring: {companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
